import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import {
  statusBadgeClasses,
  type StatusBadgeTone,
} from '~/components/ui/statusBadgeClasses';
import { SummaryPanel } from '~/pages/buy-lists/sections/SummaryPanel';
import {
  SummaryProductsPanel,
  type SummaryProduct,
} from '~/pages/buy-lists/sections/SummaryProductsPanel';

export type RecipientContact = {
  id: string;
  name: string;
};

export type RecipientQuoteStatus = {
  label: string;
  tone: StatusBadgeTone;
  respondPath?: string;
  respondLabel?: string;
};

export type RecipientGroup = {
  organizationId: string;
  organizationLabel: string;
  contacts: RecipientContact[];
  quoteStatus?: RecipientQuoteStatus;
};

type RequestForQuoteSummaryPanelsProps = {
  products: SummaryProduct[];
  recipientGroups: RecipientGroup[];
  productsTestIdPrefix?: string;
};

function RecipientGroupRow({
  recipientGroup,
}: Readonly<{ recipientGroup: RecipientGroup }>) {
  const { t } = useTranslation('inventory');
  const quoteStatus = recipientGroup.quoteStatus;
  const respondPath = quoteStatus?.respondPath;

  return (
    <li className="rounded-lg border border-neut-border px-3 py-2.5">
      <div
        className="flex items-center justify-between gap-2"
        data-testid="buy-list-recipient-organization-row"
      >
        <p className="truncate text-sm font-semibold text-neut-prim">
          {recipientGroup.organizationLabel}
        </p>
        {quoteStatus && (
          <span className={`shrink-0 ${statusBadgeClasses(quoteStatus.tone)}`}>
            {quoteStatus.label}
          </span>
        )}
      </div>
      <div
        className="mt-2 flex items-center justify-between gap-2"
        data-testid="buy-list-recipient-contacts-row"
      >
        {recipientGroup.contacts.length === 0 ? (
          <p className="text-xs text-neut-sec">
            {t('inventory.buy_list.request_for_quote.no_contacts')}
          </p>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {recipientGroup.contacts.map((contact) => (
              <li
                key={contact.id}
                className="rounded-full bg-prim-50 px-2.5 py-1 text-xs font-medium text-prim-800"
              >
                {contact.name.trim() ||
                  t('inventory.buy_list.request_for_quote.unnamed_contact')}
              </li>
            ))}
          </ul>
        )}
        {respondPath && (
          <Link
            to={respondPath}
            className="shrink-0 rounded-lg border border-prim px-2.5 py-1 text-xs font-medium text-prim hover:bg-prim-50"
          >
            {quoteStatus?.respondLabel}
          </Link>
        )}
      </div>
    </li>
  );
}

export function RequestForQuoteSummaryPanels({
  products,
  recipientGroups,
  productsTestIdPrefix,
}: Readonly<RequestForQuoteSummaryPanelsProps>) {
  const { t } = useTranslation('inventory');

  return (
    <div className="grid gap-4 lg:h-full lg:grid-cols-2">
      <SummaryProductsPanel
        title={t('inventory.buy_list.request_for_quote.summary_products')}
        products={products}
        testIdPrefix={productsTestIdPrefix}
      />

      <SummaryPanel
        title={t('inventory.buy_list.request_for_quote.summary_sellers')}
        count={recipientGroups.length}
        icon={<Building2 size={16} />}
      >
        <ul className="flex flex-col gap-2">
          {recipientGroups.map((recipientGroup) => (
            <RecipientGroupRow
              key={recipientGroup.organizationId}
              recipientGroup={recipientGroup}
            />
          ))}
        </ul>
      </SummaryPanel>
    </div>
  );
}
